/**
 * DictionariesPage Component
 *
 * Premium user dictionary management page.
 * Allows users to:
 * - View their custom dictionaries (max 5)
 * - Upload new dictionaries (JSON, CSV, XML, max 10 MB)
 * - See import reports with skipped words count
 * - Delete custom dictionaries
 *
 * @validates Requirements 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7
 */

import { useCallback, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type Dictionary,
  type ImportResult,
  VALIDATION,
  DictionaryType,
} from '@spellion/shared';
import { useStore } from '../store';
import { useTranslation } from '../hooks/useTranslation';

// ============================================================================
// Types
// ============================================================================

interface DictionaryApiResponse {
  success: boolean;
  data?: Dictionary[];
  error?: string;
}

interface ImportApiResponse {
  success: boolean;
  data?: ImportResult & { dictionary: Dictionary };
  error?: string;
}

interface DeleteApiResponse {
  success: boolean;
  error?: string;
}

interface WordsApiResponse {
  success: boolean;
  data?: {
    dictionaryId: string;
    dictionaryName: string;
    words: string[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  };
  error?: string | { code: string; message: string };
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface UploadState {
  status: UploadStatus;
  progress: number;
  error: string | null;
  result: ImportResult | null;
}

// ============================================================================
// Constants
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const ACCEPTED_EXTENSIONS = ['.json'];

const FILE_TYPE_LABELS: Record<string, string> = {
  json: 'JSON',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number, locale: string = 'fr'): string {
  if (bytes === 0) return locale === 'fr' ? '0 octets' : '0 bytes';
  const k = 1024;
  const sizes = locale === 'fr' ? ['octets', 'Ko', 'Mo', 'Go'] : ['bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.slice(lastDot).toLowerCase() : '';
}

/**
 * Format date in locale
 */
function formatDate(dateString: string, locale: string = 'fr'): string {
  return new Date(dateString).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Extract error message from API error response
 */
function getErrorMessage(error: string | { code: string; message: string } | undefined, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || fallback;
}

// ============================================================================
// DictionariesPage Component
// ============================================================================

export function DictionariesPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t, currentLanguage } = useTranslation();

  // Store state
  const token = useStore((state) => state.token);
  const isPremiumFn = useStore((state) => state.isPremium);
  const isPremium = isPremiumFn();
  const customDictionaries = useStore((state) => state.customDictionaries);
  const setCustomDictionaries = useStore((state) => state.setCustomDictionaries);
  const addCustomDictionary = useStore((state) => state.addCustomDictionary);
  const removeCustomDictionary = useStore((state) => state.removeCustomDictionary);

  // Local state
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    error: null,
    result: null,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dictionaryName, setDictionaryName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Preview modal state
  const [previewDictId, setPreviewDictId] = useState<string | null>(null);
  const [previewDictName, setPreviewDictName] = useState<string>('');
  const [previewWords, setPreviewWords] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewTotalPages, setPreviewTotalPages] = useState(0);
  const [previewSearch, setPreviewSearch] = useState('');

  // Format guide state
  const [showFormatGuide, setShowFormatGuide] = useState(false);

  // Computed values
  const canUpload = customDictionaries.length < VALIDATION.MAX_CUSTOM_DICTIONARIES;
  const remainingSlots = VALIDATION.MAX_CUSTOM_DICTIONARIES - customDictionaries.length;

  /**
   * Build request headers with auth token
   */
  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  /**
   * Fetch user's custom dictionaries
   */
  const fetchDictionaries = useCallback(async () => {
    setIsLoadingDictionaries(true);
    setLoadError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/dictionaries`, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data: DictionaryApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('dictionaries.list.error.loading'));
      }

      if (data.success && data.data) {
        const custom = data.data.filter((d) => d.type === DictionaryType.CUSTOM);
        setCustomDictionaries(custom);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      setLoadError(message);
    } finally {
      setIsLoadingDictionaries(false);
    }
  }, [getHeaders, setCustomDictionaries]);

  // Fetch dictionaries on mount
  useEffect(() => {
    if (isPremium) {
      fetchDictionaries();
    }
  }, [isPremium, fetchDictionaries]);

  // Redirect non-premium users
  useEffect(() => {
    if (!isPremium) {
      navigate('/subscription');
    }
  }, [isPremium, navigate]);

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file extension
      const extension = getFileExtension(file.name);
      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        setUploadState({
          status: 'error',
          progress: 0,
          error: t('dictionaries.upload.errors.unsupportedFormat').replace('{formats}', ACCEPTED_EXTENSIONS.join(', ')),
          result: null,
        });
        return;
      }

      // Validate file size (10 MB max)
      if (file.size > VALIDATION.MAX_DICTIONARY_FILE_SIZE) {
        setUploadState({
          status: 'error',
          progress: 0,
          error: t('dictionaries.upload.errors.fileTooLarge').replace('{size}', formatFileSize(VALIDATION.MAX_DICTIONARY_FILE_SIZE, currentLanguage)),
          result: null,
        });
        return;
      }

      setSelectedFile(file);
      // Set default name from filename (without extension)
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      setDictionaryName(baseName);
      setUploadState({ status: 'idle', progress: 0, error: null, result: null });
    },
    []
  );

  /**
   * Handle dictionary upload
   * @validates Requirements 5.2, 5.3, 5.4, 5.5, 5.6
   */
  const handleUpload = useCallback(async () => {
    if (!selectedFile || !dictionaryName.trim()) return;

    // Check max dictionaries limit
    if (!canUpload) {
      setUploadState({
        status: 'error',
        progress: 0,
        error: t('dictionaries.upload.errors.maxDictionaries').replace('{max}', String(VALIDATION.MAX_CUSTOM_DICTIONARIES)),
        result: null,
      });
      return;
    }

    setUploadState({
      status: 'uploading',
      progress: 0,
      error: null,
      result: null,
    });

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', dictionaryName.trim());

      // Use XMLHttpRequest for upload progress
      const xhr = new XMLHttpRequest();

      const uploadPromise = new Promise<ImportApiResponse>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadState((prev) => ({ ...prev, progress }));
          }
        });

        xhr.addEventListener('load', () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(getErrorMessage(response.error, t('dictionaries.upload.errors.generic'))));
            }
          } catch {
            reject(new Error(t('dictionaries.upload.errors.invalidResponse')));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error(t('dictionaries.upload.errors.network')));
        });

        xhr.open('POST', `${API_BASE_URL}/dictionaries/import`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      const data = await uploadPromise;

      if (data.success && data.data) {
        setUploadState({
          status: 'success',
          progress: 100,
          error: null,
          result: {
            success: data.data.success,
            wordsImported: data.data.wordsImported,
            wordsSkipped: data.data.wordsSkipped,
            errors: data.data.errors,
          },
        });

        // Add to store
        addCustomDictionary(data.data.dictionary);

        // Reset form after success
        setSelectedFile(null);
        setDictionaryName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      setUploadState({
        status: 'error',
        progress: 0,
        error: message,
        result: null,
      });
    }
  }, [selectedFile, dictionaryName, canUpload, token, addCustomDictionary, t]);

  /**
   * Handle dictionary deletion
   * @validates Requirement 5.1
   */
  const handleDelete = useCallback(
    async (dictionaryId: string) => {
      setIsDeleting(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/dictionaries/${dictionaryId}`,
          {
            method: 'DELETE',
            headers: getHeaders(),
          }
        );

        const data: DeleteApiResponse = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Erreur lors de la suppression');
        }

        removeCustomDictionary(dictionaryId);
        setDeleteConfirmId(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        alert(`Erreur: ${message}`);
      } finally {
        setIsDeleting(false);
      }
    },
    [getHeaders, removeCustomDictionary]
  );

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    setUploadState({ status: 'idle', progress: 0, error: null, result: null });
    setSelectedFile(null);
    setDictionaryName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * Fetch words for preview modal
   */
  const fetchPreviewWords = useCallback(
    async (dictionaryId: string, dictionaryName: string, page: number = 1, search: string = '') => {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewDictId(dictionaryId);
      setPreviewDictName(dictionaryName);
      setPreviewPage(page);

      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '100',
        });
        if (search) {
          params.append('search', search);
        }

        const response = await fetch(
          `${API_BASE_URL}/dictionaries/${dictionaryId}/words?${params}`,
          {
            method: 'GET',
            headers: getHeaders(),
          }
        );

        const data: WordsApiResponse = await response.json();

        if (!response.ok) {
          throw new Error(getErrorMessage(data.error, t('dictionaries.preview.error')));
        }

        if (data.success && data.data) {
          setPreviewWords(data.data.words);
          setPreviewTotal(data.data.pagination.total);
          setPreviewTotalPages(data.data.pagination.totalPages);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        setPreviewError(message);
      } finally {
        setPreviewLoading(false);
      }
    },
    [getHeaders, t]
  );

  /**
   * Close preview modal
   */
  const closePreview = useCallback(() => {
    setPreviewDictId(null);
    setPreviewDictName('');
    setPreviewWords([]);
    setPreviewPage(1);
    setPreviewTotal(0);
    setPreviewTotalPages(0);
    setPreviewSearch('');
    setPreviewError(null);
  }, []);

  // Don't render for non-premium users
  if (!isPremium) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto py-8 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('dictionaries.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('dictionaries.subtitle').replace('{max}', String(VALIDATION.MAX_CUSTOM_DICTIONARIES))}
        </p>
      </div>

      {/* Upload Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-6">
        <h2 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">
          {t('dictionaries.import.title')}
        </h2>

        {!canUpload ? (
          <div className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl text-orange-700 dark:text-orange-400">
            <WarningIcon className="w-5 h-5 flex-shrink-0" />
            <p>
              {t('dictionaries.import.maxReached')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* File Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('dictionaries.import.fileLabel')}
              </label>
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS.join(',')}
                  onChange={handleFileSelect}
                  className="sr-only"
                  id="dictionary-file"
                  disabled={uploadState.status === 'uploading'}
                />

                <label
                  htmlFor="dictionary-file"
                  className={`
                    flex flex-col items-center justify-center p-8
                    border-2 border-dashed rounded-xl cursor-pointer
                    transition-colors duration-150
                    ${
                      selectedFile
                        ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-slate-300 dark:border-slate-600 hover:border-primary-300 dark:hover:border-primary-500 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }
                    ${uploadState.status === 'uploading' ? 'pointer-events-none opacity-60' : ''}
                  `}
                >
                  {selectedFile ? (
                    <>
                      <FileIcon className="w-10 h-10 text-primary-500 dark:text-primary-400 mb-2" />
                      <p className="font-medium text-slate-900 dark:text-white">{selectedFile.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {formatFileSize(selectedFile.size, currentLanguage)} •{' '}
                        {FILE_TYPE_LABELS[getFileExtension(selectedFile.name).slice(1)] || 'File'}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          resetUpload();
                        }}
                        className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        {t('dictionaries.import.changeFile')}
                      </button>
                    </>
                  ) : (
                    <>
                      <UploadIcon className="w-10 h-10 text-slate-400 dark:text-slate-500 mb-2" />
                      <p className="font-medium text-slate-700 dark:text-slate-300">
                        {t('dictionaries.import.dragDrop')}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t('dictionaries.import.formats').replace('{size}', formatFileSize(VALIDATION.MAX_DICTIONARY_FILE_SIZE, currentLanguage))}
                      </p>
                    </>
                  )}
                </label>
              </div>

              {/* Format Guide Toggle */}
              <button
                type="button"
                onClick={() => setShowFormatGuide(!showFormatGuide)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 mt-2"
              >
                <InfoIcon className="w-4 h-4" />
                {showFormatGuide ? t('dictionaries.formatGuide.hideGuide') : t('dictionaries.formatGuide.showGuide')}
              </button>

              {/* Format Guide */}
              {showFormatGuide && (
                <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl space-y-4">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-1">{t('dictionaries.formatGuide.title')}</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{t('dictionaries.formatGuide.description')}</p>
                  </div>

                  {/* JSON with definitions Example */}
                  <div>
                    <h5 className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">{t('dictionaries.formatGuide.jsonWithDef.title')}</h5>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">{t('dictionaries.formatGuide.jsonWithDef.description')}</p>
                    <pre className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto whitespace-pre">
                      {t('dictionaries.formatGuide.jsonWithDef.example')}
                    </pre>
                  </div>

                  {/* JSON without definitions Example */}
                  <div>
                    <h5 className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">{t('dictionaries.formatGuide.jsonNoDef.title')}</h5>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">{t('dictionaries.formatGuide.jsonNoDef.description')}</p>
                    <pre className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">
                      {t('dictionaries.formatGuide.jsonNoDef.example')}
                    </pre>
                  </div>

                  {/* Tips */}
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-600">
                    <h5 className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">{t('dictionaries.formatGuide.tips.title')}</h5>
                    <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-primary-500 mt-0.5">•</span>
                        {t('dictionaries.formatGuide.tips.uppercase')}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-500 mt-0.5">•</span>
                        {t('dictionaries.formatGuide.tips.accents')}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-500 mt-0.5">•</span>
                        {t('dictionaries.formatGuide.tips.duplicates')}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-500 mt-0.5">•</span>
                        {t('dictionaries.formatGuide.tips.minLength')}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary-500 mt-0.5">•</span>
                        {t('dictionaries.formatGuide.tips.definitions')}
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Dictionary Name Input */}
            {selectedFile && (
              <div className="space-y-2">
                <label
                  htmlFor="dictionary-name"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {t('dictionaries.import.nameLabel')}
                </label>
                <input
                  type="text"
                  id="dictionary-name"
                  value={dictionaryName}
                  onChange={(e) => setDictionaryName(e.target.value)}
                  placeholder={t('dictionaries.import.namePlaceholder')}
                  className="
                    w-full px-4 py-3 
                    border border-slate-300 dark:border-slate-600 rounded-xl
                    bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                    disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed
                  "
                  disabled={uploadState.status === 'uploading'}
                  maxLength={100}
                />
              </div>
            )}

            {/* Upload Progress */}
            {uploadState.status === 'uploading' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">{t('dictionaries.import.progress')}</span>
                  <span className="font-medium text-primary-600 dark:text-primary-400">
                    {uploadState.progress}%
                  </span>
                </div>
                <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${uploadState.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Upload Error */}
            {uploadState.status === 'error' && uploadState.error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
                <ErrorIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{t('dictionaries.upload.errors.generic')}</p>
                  <p className="text-sm">{uploadState.error}</p>
                </div>
                <button
                  type="button"
                  onClick={resetUpload}
                  className="ml-auto p-1 hover:bg-red-100 dark:hover:bg-red-800/30 rounded transition-colors"
                  aria-label={t('common.close')}
                >
                  <CloseIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Upload Success with Import Report */}
            {uploadState.status === 'success' && uploadState.result && (
              <div className="p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl">
                <div className="flex items-start gap-3">
                  <CheckIcon className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-success-700 dark:text-success-400">
                      {t('dictionaries.upload.success.title')}
                    </p>
                    <div className="mt-2 text-sm text-success-600 dark:text-success-500 space-y-1">
                      <p>
                        <strong>{uploadState.result.wordsImported.toLocaleString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US')}</strong>{' '}
                        {t('dictionaries.upload.success.wordsImported').replace('{count}', '')}
                      </p>
                      {uploadState.result.wordsSkipped > 0 && (
                        <div className="text-orange-600 dark:text-orange-400">
                          <p>
                            <strong>{uploadState.result.wordsSkipped.toLocaleString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US')}</strong>{' '}
                            {t('dictionaries.upload.success.wordsSkipped').replace('{count}', '')}
                          </p>
                          {uploadState.result.errors && uploadState.result.errors.length > 0 && (
                            <details className="mt-2">
                              <summary className="cursor-pointer hover:text-orange-700 dark:hover:text-orange-300 font-medium">
                                {t('dictionaries.upload.success.showSkipped')} ({Math.min(uploadState.result.errors.length, 10)})
                              </summary>
                              <ul className="mt-2 ml-4 space-y-1 text-xs bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
                                {uploadState.result.errors.map((error, index) => (
                                  <li key={index} className="font-mono">
                                    {error}
                                  </li>
                                ))}
                                {uploadState.result.wordsSkipped > 10 && (
                                  <li className="text-orange-500 dark:text-orange-400 italic">
                                    {t('dictionaries.upload.success.andMore').replace('{count}', String(uploadState.result.wordsSkipped - 10))}
                                  </li>
                                )}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetUpload}
                    className="p-1 hover:bg-success-100 dark:hover:bg-success-800/30 rounded transition-colors"
                    aria-label={t('common.close')}
                  >
                    <CloseIcon className="w-4 h-4 text-success-600 dark:text-success-400" />
                  </button>
                </div>
              </div>
            )}

            {/* Upload Button */}
            {selectedFile && uploadState.status !== 'success' && (
              <button
                type="button"
                onClick={handleUpload}
                disabled={
                  !dictionaryName.trim() || uploadState.status === 'uploading'
                }
                className="
                  w-full px-6 py-3
                  bg-primary-500 text-white font-medium rounded-xl
                  hover:bg-primary-600 transition-colors duration-150
                  disabled:bg-slate-300 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2
                "
              >
                {uploadState.status === 'uploading' ? (
                  <>
                    <SpinnerIcon className="w-5 h-5 animate-spin" />
                    {t('dictionaries.import.uploading')}
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-5 h-5" />
                    {t('dictionaries.import.button')}
                  </>
                )}
              </button>
            )}

            {/* Remaining slots info */}
            <p className="text-sm text-slate-500 text-center">
              {(() => {
                const parts = t('dictionaries.import.slotsRemaining').split(' | ');
                const template = remainingSlots <= 1 ? parts[0] : (parts[1] ?? parts[0]);
                return template?.replace('{count}', String(remainingSlots)) ?? '';
              })()}
            </p>
          </div>
        )}
      </div>

      {/* Dictionaries List Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-6">
        <h2 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">
          {t('dictionaries.list.title')} ({t('dictionaries.list.count').replace('{count}', String(customDictionaries.length)).replace('{max}', String(VALIDATION.MAX_CUSTOM_DICTIONARIES))})
        </h2>

        {isLoadingDictionaries ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerIcon className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ErrorIcon className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-slate-600 dark:text-slate-400 mb-4">{loadError}</p>
            <button
              type="button"
              onClick={fetchDictionaries}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : customDictionaries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <BookIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-600 dark:text-slate-400 mb-2">{t('dictionaries.list.empty.title')}</p>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {t('dictionaries.list.empty.message')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {customDictionaries.map((dictionary) => (
              <DictionaryCard
                key={dictionary.id}
                dictionary={dictionary}
                isDeleting={deleteConfirmId === dictionary.id && isDeleting}
                showDeleteConfirm={deleteConfirmId === dictionary.id}
                onDeleteClick={() => setDeleteConfirmId(dictionary.id)}
                onDeleteConfirm={() => handleDelete(dictionary.id)}
                onDeleteCancel={() => setDeleteConfirmId(null)}
                onPreviewClick={() => fetchPreviewWords(dictionary.id, dictionary.name)}
                t={t}
                currentLanguage={currentLanguage}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick Navigation */}
      <div className="flex flex-wrap gap-4 justify-center">
        <Link
          to="/"
          className="
            inline-flex items-center gap-2 px-4 py-2
            text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400
            hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors
          "
        >
          <SearchIcon className="w-5 h-5" />
          {t('dictionaries.quickLinks.search')}
        </Link>
        <Link
          to="/account"
          className="
            inline-flex items-center gap-2 px-4 py-2
            text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400
            hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors
          "
        >
          <UserIcon className="w-5 h-5" />
          {t('dictionaries.quickLinks.account')}
        </Link>
      </div>

      {/* Preview Modal */}
      {previewDictId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                  {previewDictName}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {previewTotal.toLocaleString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US')} mots
                </p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <CloseIcon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            {/* Search */}
            <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-700">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={previewSearch}
                  onChange={(e) => {
                    setPreviewSearch(e.target.value);
                    fetchPreviewWords(previewDictId, previewDictName, 1, e.target.value);
                  }}
                  placeholder="Rechercher un mot..."
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Words List */}
            <div className="flex-1 overflow-y-auto p-6">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerIcon className="w-8 h-8 text-primary-500 animate-spin" />
                </div>
              ) : previewError ? (
                <div className="text-center py-12 text-red-600 dark:text-red-400">
                  <ErrorIcon className="w-12 h-12 mx-auto mb-3 text-red-400" />
                  <p>{previewError}</p>
                </div>
              ) : previewWords.length === 0 ? (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <p>Aucun mot trouvé</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {previewWords.map((word) => (
                    <div
                      key={word}
                      className="px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg text-sm font-mono text-slate-700 dark:text-slate-300 truncate"
                      title={word}
                    >
                      {word}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {previewTotalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Page {previewPage} sur {previewTotalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fetchPreviewWords(previewDictId, previewDictName, previewPage - 1, previewSearch)}
                    disabled={previewPage <= 1}
                    className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchPreviewWords(previewDictId, previewDictName, previewPage + 1, previewSearch)}
                    disabled={previewPage >= previewTotalPages}
                    className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DictionaryCard Component
// ============================================================================

interface DictionaryCardProps {
  dictionary: Dictionary;
  isDeleting: boolean;
  showDeleteConfirm: boolean;
  onDeleteClick: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
  onPreviewClick: () => void;
  t: (key: string) => string;
  currentLanguage: string;
}

function DictionaryCard({
  dictionary,
  isDeleting,
  showDeleteConfirm,
  onDeleteClick,
  onDeleteConfirm,
  onDeleteCancel,
  onPreviewClick,
  t,
  currentLanguage,
}: DictionaryCardProps) {
  return (
    <div
      className={`
        p-4 rounded-xl border transition-all duration-200
        ${showDeleteConfirm ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}
      `}
    >
      {showDeleteConfirm ? (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-red-700 dark:text-red-400 font-medium">
            {t('dictionaries.card.deleteConfirm').replace('{name}', dictionary.name)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDeleteCancel}
              disabled={isDeleting}
              className="
                px-4 py-2 text-sm font-medium
                text-slate-600 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-200
                bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg
                hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors
                disabled:opacity-50
              "
            >
              {t('common.cancel')}
            </button>

            <button
              type="button"
              onClick={onDeleteConfirm}
              disabled={isDeleting}
              className="
                px-4 py-2 text-sm font-medium
                text-white bg-red-500 rounded-lg
                hover:bg-red-600 transition-colors
                disabled:opacity-50
                flex items-center gap-2
              "
            >
              {isDeleting ? (
                <>
                  <SpinnerIcon className="w-4 h-4 animate-spin" />
                  {t('dictionaries.card.deleting')}
                </>
              ) : (
                <>
                  <TrashIcon className="w-4 h-4" />
                  {t('common.delete')}
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-slate-900 dark:text-white truncate">
                {dictionary.name}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('dictionaries.card.words').replace('{count}', dictionary.wordCount.toLocaleString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US'))} • {t('dictionaries.card.addedOn').replace('{date}', formatDate(dictionary.createdAt, currentLanguage))}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onPreviewClick}
              className="
                p-2 text-slate-400 dark:text-slate-500 hover:text-primary-600 dark:hover:text-primary-400
                hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors
                min-h-touch min-w-touch
                flex items-center justify-center
              "
              aria-label={`Voir les mots de ${dictionary.name}`}
              title="Voir les mots"
            >
              <EyeIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onDeleteClick}
              className="
                p-2 text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400
                hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors
                min-h-touch min-w-touch
                flex items-center justify-center
              "
              aria-label={`${t('common.delete')} ${dictionary.name}`}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Icon Components
// ============================================================================

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

export default DictionariesPage;
